import { useState } from "react";
import { EfMark } from "@/components/Logo";
import { Btn } from "@/components/ui";
import { IcoLayers, IcoSparkle, IcoWand } from "@/components/icons";

const STORAGE_KEY = "ef_onboarded";

const STEPS = [
  { icon: <EfMark size={44} />, title: "Welcome to EditsForge", body: "Your web-based NLE with AI automation. CapCut + Descript + Runway — self-hosted." },
  { icon: <IcoLayers size={34} />, title: "Multi-track timeline", body: "Cut, ripple, slip and trim across video, audio and caption tracks — frame-precise." },
  { icon: <IcoSparkle size={34} />, title: "AI on every panel", body: "Auto-transcribe, remove silences, detect scenes, match color — one click each." },
  { icon: <IcoWand size={34} />, title: "Director mode", body: "Describe the edit in plain language and let the agent assemble it for you." },
];

// First-visit product tour. Shown once; dismissal is remembered in localStorage.
export function OnboardingOverlay({ enabled }: { enabled: boolean }) {
  const [open, setOpen] = useState(() => {
    if (!enabled) return false;
    try {
      return localStorage.getItem(STORAGE_KEY) === null;
    } catch {
      return false;
    }
  });
  const [step, setStep] = useState(0);

  if (!enabled || !open) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      /* ignore */
    }
    setOpen(false);
  };

  const last = step === STEPS.length - 1;
  const s = STEPS[step]!;

  return (
    <div className="fixed inset-0 z-[9800] flex items-center justify-center p-5" style={{ background: "rgba(6,8,12,0.9)" }}>
      <div className="relative w-full max-w-[480px] overflow-hidden rounded-2xl border border-ef-border bg-ef-panel px-[52px] py-[44px] text-center shadow-[0_24px_64px_rgba(0,0,0,0.5)] [animation:slideDown_0.2s_ease]">
        {/* Gradient top accent bar */}
        <div className="absolute inset-x-0 top-0 h-0.5" style={{ background: "linear-gradient(90deg,var(--accent),var(--accent2))" }} />
        {step === 0 && (
          <button type="button" onClick={dismiss} className="absolute right-4 top-4 text-xs text-ef-textMuted hover:text-ef-text">
            Skip intro
          </button>
        )}
        {/* Progress dots, above the icon */}
        <div className="mb-9 flex justify-center gap-2">
          {STEPS.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all ${i === step ? "w-6 bg-ef-accent" : "w-1.5 bg-ef-border"}`}
            />
          ))}
        </div>
        <div className="mb-5 flex justify-center text-ef-accent">{s.icon}</div>
        <h2 className="mb-2 font-display text-xl font-bold tracking-[-0.03em] text-ef-text">{s.title}</h2>
        <p className="mx-auto mb-7 max-w-[340px] text-sm leading-relaxed text-ef-textDim">{s.body}</p>

        <div className="flex items-center justify-center gap-2.5">
          {step > 0 && (
            <Btn variant="ghost" onClick={() => setStep((v) => v - 1)}>
              ← Back
            </Btn>
          )}
          {last ? (
            <Btn variant="primary" size="md" onClick={dismiss}>
              Get started →
            </Btn>
          ) : (
            <Btn variant="primary" size="md" onClick={() => setStep((v) => v + 1)}>
              Next →
            </Btn>
          )}
        </div>
      </div>
    </div>
  );
}
